import { useState, useEffect, useCallback } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { auth, db } from "./firebase";
import { C } from "./constants/theme";
import { AppThemeProvider } from "./hooks/useAppTheme";
import { fbList, fbAdd, fbUpdate } from "./hooks/useFirebase";
import { useFilaSync } from "./hooks/useFilaSync";
import { useNotificacoes, solicitarPermissaoNotificacao } from "./hooks/useNotificacoes";
import { useNotificacoesEventos } from "./hooks/useNotificacoesEventos";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { useNotificacaoAssinatura } from "./hooks/useNotificacaoAssinatura";
import { useAvisoAtualizacao } from "./hooks/useAvisoAtualizacao";
import { useFavoritos } from "./hooks/useFavoritos";
import { useTour } from "./hooks/useTour";
import { I, BOTTOM_NAV, MAIS_IDS } from "./constants/nav";
import { MobileMore } from "./components/MobileMore";
import { MobileHeaderShortcuts, isMobileShortcutTourTarget, MOBILE_HEADER_SHORTCUTS } from "./components/MobileHeaderShortcuts";
import { espelharLojaPublica } from "./utils/lojaPublica";
import { mpHabilitado, capturarRetornoConexao, retornoPendente } from "./utils/mercadoPago";
import { pagamentosExtrasHabilitados, capturarRetornoPagBank, retornoPagBankPendente } from "./utils/pagamentos";
import { precisaConfirmarEmail } from "./utils/printControlAuth";
import { mostrarAvisoTrial } from "./utils/avisoTrial";
import Login from "./Login";
import { ConfirmarEmailConta } from "./components/ConfirmarEmailConta";
import Dashboard   from "./components/Dashboard";
import HomeMobileExtras from "./components/HomeMobileExtras";
import { Pedidos }     from "./components/Pedidos";
import { Filamentos }  from "./components/Filamentos";
import { Impressoras } from "./components/Impressoras";
import { Materiais }   from "./components/Materiais";
import { Produtos }    from "./components/Produtos";
import { Financeiro }  from "./components/Financeiro";
import Agenda      from "./components/Agenda";
import { Calculadora } from "./components/Calculadora";
import Configuracoes  from "./components/Configuracoes";
import { ToolDialog } from "./components/ToolWorkspace";
import { AppHeader, CompanyBrand, HeaderIcon } from "./components/AppHeader";
import { WebNavigation } from "./components/WebNavigation";
import Clientes       from "./components/Clientes";
import { Caixa }         from "./components/Caixa";
import { FilaImpressao } from "./components/FilaImpressao";
import { Site }          from "./components/Site";
import LojasParceiras    from "./components/LojasParceiras";
import PainelLojaParceira, { buscarLojaParceira } from "./components/PainelLojaParceira";
import PlanoExpirado  from "./components/PlanoExpirado";
import EscolherPlano  from "./components/EscolherPlano";
import { PLANOS_PAGOS } from "./components/PlanCards";
import AdminPanel     from "./components/AdminPanel";
import { ToastContainer } from "./components/ui/ToastContainer";
import { NotasIA } from "./components/NotasIA";
import { TourOverlay } from "./components/ui/TourOverlay";
import { TourPdvInteractive } from "./components/ui/TourPdvInteractive";
import { TourFilamentoInteractive } from "./components/ui/TourFilamentoInteractive";
import { TourCalculadoraInteractive } from "./components/ui/TourCalculadoraInteractive";
import { TourImpressoraInteractive } from "./components/ui/TourImpressoraInteractive";

const TOUR_INTERACTIVE_COMPONENTS = {
  "interactive-pdv": TourPdvInteractive,
  "interactive-filamento": TourFilamentoInteractive,
  "interactive-calculadora": TourCalculadoraInteractive,
  "interactive-impressora": TourImpressoraInteractive,
};

const ADMIN_EMAIL   = "printcontrol2026@gmail.com";
const EXEMPT_EMAILS = ["printcontrol2026@gmail.com","matriz03d@gmail.com","felipe16otto@gmail.com","alexoliver1124@gmail.com"];
export const APP_VERSION  = "1.3.6";

function isAndroidApp() {
  return window.Capacitor?.getPlatform?.() === "android";
}

function isMobileScreen() {
  return isAndroidApp() || window.innerWidth < 768;
}

/* ── Desktop config ───────────────────────────────────────── */
const TABS = [
  { id:"dashboard",   label:"Dashboard",   icon:"📊" },
  { id:"calc",        label:"Calculadora", icon:"🧮" },
  { id:"produtos",    label:"Produtos",    icon:"📦" },
  { id:"pedidos",     label:"Pedidos",     icon:"📋" },
  { id:"site",        label:"Site",        icon:"🌐" },
  { id:"financeiro",  label:"Financeiro",  icon:"💲" },
  { id:"caixa",       label:"PDV",         icon:"🛒" },
  { id:"fila",        label:"Fila Impressão", icon:"🖨️" },
];
// Só aparece com "Lojas parceiras" ligado em Configurações → Vendas online
const TAB_PARCEIROS = { id:"parceiros", label:"Lojas parceiras", icon:"🤝" };

const TAB_COLOR = {
  dashboard:"var(--pc-ui-e8ecf4, #e8ecf4)", pedidos:C.accent, clientes:"#25d366", filamentos:C.accent2, impressoras:C.accent3,
  materiais:"#e879f9", produtos:"var(--pc-ui-f59e0b, #f59e0b)", site:"var(--pc-ui-0ea5e9, #0ea5e9)", financeiro:C.success, caixa:"#f97316", calc:C.warning,
  agenda:"var(--pc-ui-38bdf8, #38bdf8)", relatorio:"#f0abfc", fila:"var(--pc-ui-38bdf8, #38bdf8)", parceiros:C.accent2,
};

/* ── Mobile config ────────────────────────────────────────── */
// Ícones, barra inferior e hub "Mais" moram em constants/nav.js (não aqui)
// para o HomeMobileExtras poder importá-los sem criar dependência circular.

const TAB_LABELS = {
  dashboard:"Dashboard", pedidos:"Pedidos", clientes:"Clientes", filamentos:"Filamentos",
  impressoras:"Impressoras", materiais:"Materiais", produtos:"Produtos", site:"Site",
  financeiro:"Financeiro", caixa:"PDV", agenda:"Agenda", calc:"Calculadora", config:"Configurações", fila:"Fila de Impressão", parceiros:"Lojas parceiras",
};

// Destinos fixos da busca global — telas inteiras (não itens de coleção),
// encontradas pelo nome ou por sinônimos comuns.
const BUSCA_DESTINOS = [
  { label:"Financeiro",         tab:"financeiro",  icon:"💲", palavras:["financeiro","caixa","fluxo"] },
  { label:"PDV",                tab:"caixa",       icon:"🛒", palavras:["pdv","caixa","venda"] },
  { label:"Configurações",      tab:"config",      icon:"⚙️", palavras:["configuracoes","configuração","ajustes","conta","assinatura","plano"] },
  { label:"Calculadora",        tab:"calc",        icon:"🧮", palavras:["calculadora","custo","preco","orcamento","orcamentos","orçamento","orçamentos"] },
  { label:"Fila de Impressão",  tab:"fila",        icon:"🖨️", palavras:["fila","impressao","impressões"] },
  { label:"Loja Online",        tab:"site",        icon:"🌐", palavras:["loja","site","online"] },
  { label:"Filamentos",         tab:"filamentos",  icon:"🧵", palavras:["filamentos"] },
  { label:"Materiais",          tab:"materiais",   icon:"🔧", palavras:["materiais"] },
  { label:"Produtos",           tab:"produtos",    icon:"📦", palavras:["produtos"] },
];

/* ── Shared: Update Modal ─────────────────────────────────── */
function UpdateModal({ updateInfo, downloadProgress }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(4,17,31,0.97)", zIndex:99999, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ maxWidth:400, width:"100%", background:"var(--pc-ui-0a1628, #0a1628)", border:"1px solid rgba(0,212,170,0.3)", borderRadius:20, padding:"40px 32px", textAlign:"center", boxShadow:"0 32px 80px #000" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🚀</div>
        <div style={{ fontWeight:900, fontSize:18, color:"var(--pc-ui-f1f5f9, #f1f5f9)", marginBottom:8, letterSpacing:1 }}>ATUALIZAÇÃO DISPONÍVEL</div>
        <div style={{ color:"#64748b", fontSize:13, marginBottom:4 }}>Versão <strong style={{ color:"var(--pc-ui-00d4aa, #00d4aa)" }}>v{updateInfo.version}</strong> disponível</div>
        <div style={{ color:"#475569", fontSize:11, marginBottom:24 }}>Você está na v{updateInfo.current}</div>
        {updateInfo.changelog && (
          <div style={{ background:"rgba(0,212,170,0.05)", border:"1px solid rgba(0,212,170,0.15)", borderRadius:10, padding:"10px 14px", fontSize:11, color:"var(--pc-ui-94a3b8, #94a3b8)", marginBottom:24, textAlign:"left", lineHeight:1.6 }}>
            {updateInfo.changelog}
          </div>
        )}
        {window.electronAPI ? (
          downloadProgress === 'ready' ? (
            <button onClick={() => window.electronAPI.installUpdate()} style={{ width:"100%", background:"linear-gradient(135deg,var(--pc-ui-22c55e, #22c55e),#16a34a)", border:"none", color:"#fff", borderRadius:12, padding:"14px 0", fontSize:14, fontWeight:900, cursor:"pointer" }}>✅ Instalar agora</button>
          ) : downloadProgress === 'error' ? (
            <button onClick={() => window.electronAPI.openExternal(updateInfo.downloadUrl)} style={{ width:"100%", background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", color:"var(--pc-ui-04111f, #04111f)", borderRadius:12, padding:"14px 0", fontSize:14, fontWeight:900, cursor:"pointer" }}>⬇️ Baixar manualmente</button>
          ) : (
            <div style={{ background:"var(--pc-ui-0f2847, #0f2847)", borderRadius:10, padding:"14px 16px" }}>
              <div style={{ color:"var(--pc-ui-94a3b8, #94a3b8)", fontSize:12, marginBottom:8 }}>{downloadProgress === null ? "Preparando download..." : `Baixando... ${downloadProgress}%`}</div>
              <div style={{ background:"#1e3a5f", borderRadius:4, height:6, overflow:"hidden" }}>
                <div style={{ background:"linear-gradient(90deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", borderRadius:4, height:6, width:`${downloadProgress||0}%`, transition:"width 0.3s" }} />
              </div>
            </div>
          )
        ) : isAndroidApp() ? (
          <div style={{ background:"var(--pc-ui-0f2847, #0f2847)", borderRadius:10, padding:"14px 16px" }}>
            <div style={{ color:"var(--pc-ui-94a3b8, #94a3b8)", fontSize:12, marginBottom:10 }}>⏳ Baixando atualização...</div>
            <button onClick={() => window.open(updateInfo.downloadUrl, '_system')} style={{ width:"100%", background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", color:"var(--pc-ui-04111f, #04111f)", borderRadius:10, padding:"12px 0", fontSize:13, fontWeight:900, cursor:"pointer" }}>
              ⬇️ Toque para baixar
            </button>
          </div>
        ) : (
          <button onClick={() => window.open(updateInfo.downloadUrl, "_blank")} style={{ width:"100%", background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", color:"var(--pc-ui-04111f, #04111f)", borderRadius:12, padding:"14px 0", fontSize:14, fontWeight:900, cursor:"pointer" }}>⬇️ Baixar atualização</button>
        )}
        <div style={{ color:"var(--pc-ui-334155, #334155)", fontSize:11, marginTop:12 }}>A atualização é obrigatória para continuar usando o app.</div>
      </div>
    </div>
  );
}

/* ── Shared: Tab Content ──────────────────────────────────── */
function TabContent({ tab, coresCustom, marcasSalvas, addCor, addMarca, perfil, uid, onUpdate, onBadgeUpdate, onPersonalizar, isMobile, clientesGlobal, onGoTab, onAbrirBusca, onIniciarTutorial, tutorialCalculadora, notificacaoDestino, posicaoAbas, onPosicaoAbasChange, abasTravadas, onTravaAbasChange }) {
  return (
    <>
      {tab==="dashboard"   && isMobile && <HomeMobileExtras perfil={perfil} clientesGlobal={clientesGlobal} onGoTab={onGoTab} onAbrirBusca={onAbrirBusca} />}
      {tab==="dashboard"   && <Dashboard onGoTab={onGoTab} coresCustom={coresCustom} />}
      {tab==="pedidos"     && <Pedidos coresCustom={coresCustom} notificacaoDestino={notificacaoDestino} />}
      {tab==="clientes"    && <Clientes onBadgeUpdate={onBadgeUpdate} />}
      {tab==="filamentos"  && <Filamentos coresCustom={coresCustom} onAddCor={addCor} marcasSalvas={marcasSalvas} onAddMarca={addMarca} />}
      {tab==="impressoras" && <Impressoras />}
      {tab==="materiais"   && <Materiais />}
      {tab==="produtos"    && <Produtos coresCustom={coresCustom} />}
      {tab==="site"        && <Site notificacaoDestino={notificacaoDestino} />}
      {tab==="financeiro"  && <Financeiro />}
      {tab==="caixa"       && <Caixa />}
      {tab==="agenda"      && <Agenda />}
      {tab==="calc"        && <Calculadora coresCustom={coresCustom} onIrPedidos={()=>onGoTab("pedidos")} tutorialAtivo={tutorialCalculadora} />}
      {tab==="fila"        && <FilaImpressao />}
      {tab==="parceiros"   && perfil?.consignacaoAtiva && <LojasParceiras uid={uid} perfil={perfil} />}
      {tab==="config"      && <Configuracoes perfil={perfil} uid={uid} onUpdate={onUpdate} onPersonalizar={onPersonalizar} onIniciarTutorial={onIniciarTutorial} notificacaoDestino={notificacaoDestino} posicaoAbas={posicaoAbas} onPosicaoAbasChange={onPosicaoAbasChange} abasTravadas={abasTravadas} onTravaAbasChange={onTravaAbasChange} />}
    </>
  );
}

/* ── App ──────────────────────────────────────────────────── */
export default function App() {
  return <AppThemeProvider><AppContent /></AppThemeProvider>;
}

function AppContent() {
  const [userLogado, setUserLogado] = useState(null);
  const [authReady,  setAuthReady]  = useState(false);
  // Entrou com e-mail ainda não confirmado: o painel espera o código (ConfirmarEmailConta)
  const [emailPendente, setEmailPendente] = useState(false);
  const [notificacaoDestino, setNotificacaoDestino] = useState(null);
  // Conta de loja parceira não tem painel de vendedor: nada de fila, clientes, impressoras...
  const ehLojaParceira = !!userLogado?.lojaParceiraId;
  useFilaSync(!!userLogado && !ehLojaParceira);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [coresCustom, setCoresCustom] = useState(() => {
    try { return JSON.parse(localStorage.getItem("3dp_cores")) || []; } catch { return []; }
  });
  const [marcasSalvas, setMarcasSalvas] = useState(() => {
    try {
      const OLD = ["Creality","Elegoo","Sunlu","Hatchbox","3DFila"];
      const s = JSON.parse(localStorage.getItem("3dp_marcas")) || ["Bambu"];
      const c = s.filter(m => !OLD.includes(m));
      return c.length ? c : ["Bambu"];
    } catch { return ["Bambu"]; }
  });
  const [marcasDocId, setMarcasDocId] = useState(null);
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("3dp_tab") || "dashboard"; } catch { return "dashboard"; }
  });
  // Mantém a aba ativa ao recarregar (não volta ao início)
  useEffect(() => { try { localStorage.setItem("3dp_tab", tab); } catch {} }, [tab]);
  const [clientesBadge,  setClientesBadge]  = useState(0);
  const [clientesGlobal, setClientesGlobal] = useState([]);
  const [modalImpressoras, setModalImpressoras] = useState(false);
  const [impressorasCount, setImpressorasCount] = useState(null);
  const [modalAgenda, setModalAgenda] = useState(false);
  const [modalClientes, setModalClientes] = useState(false);
  const [modalFilamentos, setModalFilamentos] = useState(false);
  const [modalMateriais, setModalMateriais] = useState(false);
  const [modalNotas, setModalNotas] = useState(false);
  const [showAtalhos, setShowAtalhos] = useState(false);
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [buscaQuery, setBuscaQuery] = useState("");
  const [buscaDados, setBuscaDados] = useState(null);

  // Responsive
  const [isMobile, setIsMobile] = useState(() => isMobileScreen());
  useEffect(() => {
    const check = () => setIsMobile(isMobileScreen());
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Busca global: aberta pelo Ctrl+K (desktop) ou pelo botão 🔍 da Home (mobile)
  const abrirBusca = useCallback(() => {
    setBuscaAberta(true);
    if (!buscaDados) {
      Promise.all([
        fbList("pedidos").catch(() => []),
        fbList("produtos").catch(() => []),
        fbList("filamentos").catch(() => []),
        fbList("materiais").catch(() => []),
      ]).then(([pedidos, produtos, filamentos, materiais]) => setBuscaDados({ pedidos, produtos, filamentos, materiais }));
    }
  }, [buscaDados]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") { e.preventDefault(); abrirBusca(); }
      if (e.key === "Escape") { setBuscaAberta(false); setBuscaQuery(""); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [buscaDados, abrirBusca]);

  // Desktop-only state
  const [posicaoAbas, setPosicaoAbas] = useState(() => {
    try { return localStorage.getItem("3dp_web_posicao_abas") === "lateral" ? "lateral" : "topo"; }
    catch { return "topo"; }
  });
  const permitePosicaoAbas = !Capacitor.isNativePlatform() && !window.electronAPI;
  const abasLaterais = permitePosicaoAbas && posicaoAbas === "lateral";
  const alterarPosicaoAbas = (posicao) => {
    setPosicaoAbas(posicao);
    try { localStorage.setItem("3dp_web_posicao_abas", posicao); } catch {}
  };
  const [abasTravadas, setAbasTravadas] = useState(() => {
    try { return localStorage.getItem("3dp_web_abas_travadas") === "true"; }
    catch { return false; }
  });
  const alterarTravaAbas = (travadas) => {
    setAbasTravadas(travadas);
    try { localStorage.setItem("3dp_web_abas_travadas", String(travadas)); } catch {}
  };
  const [tabsOrdem, setTabsOrdem] = useState(() => {
    try {
      const saved = (JSON.parse(localStorage.getItem("3dp_tabs_ordem")) || []).filter(id => id !== "relatorio");
      if (!saved.length) return TABS;
      const ordered = saved.map(id => [...TABS, TAB_PARCEIROS].find(t => t.id === id)).filter(Boolean);
      const novas   = TABS.filter(t => !saved.includes(t.id));
      return [...ordered, ...novas];
    } catch { return TABS; }
  });
  // A aba de lojas parceiras entra (no fim) ou sai conforme a opção do perfil.
  const consignacaoAtiva = !!perfil?.consignacaoAtiva;
  useEffect(() => {
    if (!perfil) return;
    setTabsOrdem(ts => consignacaoAtiva === ts.some(t => t.id === "parceiros") ? ts
      : consignacaoAtiva ? [...ts, TAB_PARCEIROS] : ts.filter(t => t.id !== "parceiros"));
  }, [perfil, consignacaoAtiva]);
  const [editandoAbas, setEditandoAbas] = useState(false);
  const [showHint, setShowHint] = useState(() => !localStorage.getItem("3dp_hint_personalizar"));

  // Mobile-only state
  const [showMais, setShowMais] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const { isFavorito, toggleFavorito } = useFavoritos();

  /* ── Effects ── */
  useEffect(() => {
    if (!window.electronAPI) return;
    window.electronAPI.onUpdateAvailable(info => setUpdateInfo(info));
    window.electronAPI.onUpdateDownloading(() => setDownloadProgress(0));
    window.electronAPI.onUpdateProgress(({ percent }) => setDownloadProgress(percent));
    window.electronAPI.onUpdateReady(() => setDownloadProgress('ready'));
    window.electronAPI.onUpdateError(() => setDownloadProgress('error'));
  }, []);

  // Checagem de atualização obrigatória do APK via Firestore foi removida —
  // distribuição por APK direto (fora da Play Store) travava o acesso normal
  // ao app pra quem estava numa versão mais antiga. Quando a Play Store
  // estiver no ar, as próprias atualizações da loja cuidam disso sozinhas,
  // sem precisar dessa tela obrigatória aqui dentro.

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setEmailPendente(precisaConfirmarEmail(firebaseUser));
      if (firebaseUser) {
        try {
          const saved = JSON.parse(localStorage.getItem("3dp_user")) || JSON.parse(sessionStorage.getItem("3dp_user"));
          if (saved && saved.uid === firebaseUser.uid) {
            setUserLogado(saved);
            setAuthReady(true);
            return;
          }
          // Sem sessão salva: só loga automaticamente se já existe cadastro
          // completo (users/{uid}). Sem essa checagem, uma conta Google recém-
          // autenticada (ainda sem "empresa" preenchida, cadastro não concluído)
          // cai direto no app pulando a tela de Cadastro — e nunca cria o doc,
          // ficando invisível no painel admin. O Login.js cuida da 1ª vez.
          const snap = await getDoc(doc(db, "users", firebaseUser.uid));
          const d = snap.data();
          // cadastroCompleto false = conta criada no 1º login do Google, com
          // o formulário ainda por preencher. O documento passou a existir
          // desde o primeiro clique (pra conta aparecer no painel), então a
          // existência dele sozinha não serve mais de sinal aqui.
          if (!snap.exists() || d?.cadastroCompleto === false) {
            // Sem cadastro de vendedor, mas o e-mail é de uma loja parceira: abre o painel dela.
            const loja = await buscarLojaParceira(firebaseUser.email);
            if (loja) {
              const lojaObj = { nome:loja.nome, avatar:"🏪", uid:firebaseUser.uid, email:firebaseUser.email, lojaParceiraId:loja.id };
              sessionStorage.setItem("3dp_user", JSON.stringify(lojaObj));
              setUserLogado(lojaObj);
            }
            setAuthReady(true);
            return;
          }
          const userObj = { nome:d.nome||firebaseUser.displayName||firebaseUser.email, avatar:"🧑‍💻", uid:firebaseUser.uid, email:firebaseUser.email, empresa:d.empresa, logoUrl:d.logoUrl||"" };
          sessionStorage.setItem("3dp_user", JSON.stringify(userObj));
          setUserLogado(userObj);
        } catch (e) {
          // Não loga automaticamente se a checagem falhar (ex.: rede) — cair
          // pro comportamento antigo (logar sem checar) é o que causava o
          // bug de pular o cadastro.
          console.error("Erro ao restaurar sessão:", e);
        }
      } else {
        localStorage.removeItem("3dp_user");
        sessionStorage.removeItem("3dp_user");
        setUserLogado(null);
      }
      setAuthReady(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!userLogado?.uid) { setPerfil(null); return; }
    getDoc(doc(db, "users", userLogado.uid)).then(snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      setPerfil(d);
      // Pro painel admin saber em que plataforma/versão cada conta usa o app.
      const plataforma = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : window.electronAPI ? "windows" : "web";
      if (d.plataforma !== plataforma || d.versaoApp !== APP_VERSION || !sessionStorage.getItem("3dp_acesso")) {
        sessionStorage.setItem("3dp_acesso", "1");
        updateDoc(doc(db, "users", userLogado.uid), { plataforma, versaoApp: APP_VERSION, ultimoAcessoApp: serverTimestamp() }).catch(() => {});
      }
      // Backfill: garante que o doc público da loja exista/esteja atualizado
      // (perfil + PIX, sem segredos) para a loja pública funcionar p/ visitantes.
      espelharLojaPublica(userLogado.uid, {
        empresa: d.empresa, nome: d.nome, logoUrl: d.logoUrl,
        logoSiteUrl: d.logoSiteUrl,
        email: d.email, paymentConfig: d.paymentConfig, contato: d.contatoLoja,
      }).catch(() => {});
    });
  }, [userLogado]);

  useEffect(() => { localStorage.setItem("3dp_cores", JSON.stringify(coresCustom)); }, [coresCustom]);

  useEffect(() => {
    fbList("config").then(configs => {
      const d = configs.find(c => c.tipo === "marcas");
      if (d) {
        setMarcasDocId(d.id);
        if (d.lista?.length) { setMarcasSalvas(d.lista); localStorage.setItem("3dp_marcas", JSON.stringify(d.lista)); }
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!userLogado || ehLojaParceira) return;
    fbList("clientes").then(setClientesGlobal).catch(() => {});
  }, [userLogado, ehLojaParceira]);

  const recarregarImpressoras = () => {
    fbList("impressoras").then(list => setImpressorasCount(list.length)).catch(() => {});
  };

  useEffect(() => {
    if (!userLogado || ehLojaParceira) return;
    recarregarImpressoras();
  }, [userLogado, ehLojaParceira]);

  const buscaResultados = (() => {
    const q = buscaQuery.toLowerCase().trim();
    if (!q || !buscaDados) return [];
    const results = [];
    (buscaDados.pedidos || []).forEach(p => {
      if ((p.produto||"").toLowerCase().includes(q) || (p.cliente||"").toLowerCase().includes(q))
        results.push({ tipo:"Pedido", label:`${p.produto} — ${p.cliente}`, sub:p.status, tab:"pedidos", icon:"📋" });
    });
    (clientesGlobal || []).forEach(c => {
      if ((c.nome||"").toLowerCase().includes(q) || (c.celular||"").toLowerCase().includes(q))
        results.push({ tipo:"Cliente", label:c.nome, sub:c.celular, tab:"clientes", icon:"👥" });
    });
    (buscaDados.produtos || []).forEach(p => {
      if ((p.nome||"").toLowerCase().includes(q))
        results.push({ tipo:"Produto", label:p.nome, sub:p.preco?`R$ ${parseFloat(p.preco).toFixed(2)}`:"", tab:"produtos", icon:"📦" });
    });
    (buscaDados.filamentos || []).forEach(f => {
      const nome = f.cor || f.nome || "";
      if (nome.toLowerCase().includes(q) || (f.marca||"").toLowerCase().includes(q))
        results.push({ tipo:"Filamento", label:nome, sub:f.marca, tab:"filamentos", icon:"🧵" });
    });
    (buscaDados.materiais || []).forEach(m => {
      if ((m.nome||"").toLowerCase().includes(q))
        results.push({ tipo:"Material", label:m.nome, sub:m.quantidade!=null?`${m.quantidade} ${m.unidade||""}`.trim():"", tab:"materiais", icon:"🔧" });
    });
    BUSCA_DESTINOS.forEach(d => {
      if (d.palavras.some(p => p.includes(q) || q.includes(p)))
        results.push({ tipo:"Ir para", label:d.label, sub:"", tab:d.tab, icon:d.icon });
    });
    return results.slice(0, 10);
  })();

  useEffect(() => {
    if (!userLogado) return;
    solicitarPermissaoNotificacao();
  }, [userLogado]);

  useNotificacoes();
  useNotificacoesEventos(ehLojaParceira ? null : userLogado?.uid);
  const abrirNotificacao = useCallback(destino => {
    setNotificacaoDestino({ ...destino, recebidoEm: Date.now() });
    setShowMais(false);
    setTab(destino.tab);
  }, []);
  usePushNotifications(userLogado?.uid, abrirNotificacao);
  // Volta do OAuth do Mercado Pago (/#mp_conexao=...): sai do endereço na hora
  // e abre Configurações → Pagamentos, que conclui a conexão no backend.
  useEffect(() => { if (mpHabilitado()) capturarRetornoConexao(); }, []);
  // Mesma coisa para a volta do PagBank (/#pg_conexao=pagbank...), onde ele estiver habilitado
  useEffect(() => { if (pagamentosExtrasHabilitados()) capturarRetornoPagBank(); }, []);
  useEffect(() => {
    const mp = mpHabilitado() && retornoPendente();
    const pb = pagamentosExtrasHabilitados() && retornoPagBankPendente();
    if (userLogado && (mp || pb)) abrirNotificacao({ tab: "config", secao: "pagamentos" });
  }, [userLogado, abrirNotificacao]);
  useNotificacaoAssinatura(perfil, EXEMPT_EMAILS.includes(userLogado?.email));
  useAvisoAtualizacao(userLogado?.uid, APP_VERSION);

  /* ── Handlers ── */
  const login = (u, persist = false) => {
    if (persist) localStorage.setItem("3dp_user", JSON.stringify(u));
    else sessionStorage.setItem("3dp_user", JSON.stringify(u));
    setEmailPendente(precisaConfirmarEmail(auth.currentUser));
    setUserLogado(u);
    if (u?.empresa) setPerfil(prev => ({ ...prev, empresa:u.empresa, logoUrl:u.logoUrl||"" }));
  };

  const logout = () => {
    localStorage.removeItem("3dp_user");
    sessionStorage.removeItem("3dp_user");
    setUserLogado(null);
    setEmailPendente(false);
    setNotificacaoDestino(null);
    setPerfil(null);
    // Sem isto a sessão do Firebase continuava ativa: recarregar a página entrava de novo sem senha
    signOut(auth).catch(() => {});
  };

  const addCor = (cor) => { if (!coresCustom.find(c => c.nome === cor.nome)) setCoresCustom(cs => [...cs, cor]); };

  const addMarca = async (m) => {
    if (marcasSalvas.includes(m)) return;
    const novaLista = [...marcasSalvas, m];
    setMarcasSalvas(novaLista);
    localStorage.setItem("3dp_marcas", JSON.stringify(novaLista));
    if (marcasDocId) { await fbUpdate("config", marcasDocId, { lista:novaLista }); }
    else { const ref = await fbAdd("config", { tipo:"marcas", lista:novaLista }); setMarcasDocId(ref.id); }
  };

  // Mobile nav
  const goTab = (id) => {
    setShowAtalhos(false);
    if (id === "mais") { setShowMais(v => !v); return; }
    setTab(id);
    setShowMais(false);
  };

  // Fecha tudo que possa estar aberto (hub "Mais", modais rápidos) — usado
  // pelo tutorial guiado antes de revelar o alvo de cada passo.
  const resetTourOverlays = useCallback(() => {
    setShowAtalhos(false);
    setShowMais(false);
    setModalClientes(false);
    setModalAgenda(false);
    setModalImpressoras(false);
    setModalFilamentos(false);
    setModalMateriais(false);
    setModalNotas(false);
    setFabOpen(false);
    setEditandoAbas(false);
  }, []);

  const tour = useTour({
    uid: userLogado?.uid,
    perfil,
    setPerfil,
    isMobile,
    setTab,
    setShowMais,
    resetOverlays: resetTourOverlays,
    enabled: userLogado?.email !== ADMIN_EMAIL,
  });

  useEffect(() => {
    if (!isMobile) { setShowAtalhos(false); return; }
    if (tour.active && tour.step?.kind === "nav" && isMobileShortcutTourTarget(tour.step.target)) {
      setShowMais(false);
      setShowAtalhos(true);
    }
  },[isMobile,tour.active,tour.index,tour.step?.kind,tour.step?.target]);

  const abrirAtalho = id => {
    setShowMais(false);
    const modais = {clientes:setModalClientes,agenda:setModalAgenda,impressoras:setModalImpressoras,filamentos:setModalFilamentos,materiais:setModalMateriais,notas:setModalNotas};
    if (modais[id]) modais[id](true);
    else if (id === "busca") abrirBusca();
    else goTab(id);
  };

  /* ── Loading ── */
  if (!authReady) return (
    <div style={{ minHeight:"100dvh", background:"var(--pc-ui-060b18, #060b18)", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <div style={{ width:40, height:40, border:"3px solid rgba(0,212,170,0.15)", borderTop:"3px solid var(--pc-ui-00d4aa, #00d4aa)", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      <div style={{ color:"var(--pc-ui-334155, #334155)", fontSize:12, letterSpacing:2 }}>CARREGANDO...</div>
    </div>
  );

  if (!userLogado) return <Login onLogin={login} />;
  if (emailPendente) return <ConfirmarEmailConta email={auth.currentUser?.email || userLogado.email} onConfirmado={() => setEmailPendente(false)} onSair={logout} />;
  if (userLogado.lojaParceiraId) return <PainelLojaParceira lojaId={userLogado.lojaParceiraId} email={userLogado.email} onLogout={logout} />;

  /* ── Plan checks ── */
  const isAdmin   = userLogado?.email === ADMIN_EMAIL;
  const isExempt  = EXEMPT_EMAILS.includes(userLogado?.email);
  const trialEnds = perfil?.trialEndsAt?.toDate?.() || (perfil?.trialEndsAt ? new Date(perfil.trialEndsAt) : null);
  const diasTrial = trialEnds ? Math.max(0, Math.ceil((trialEnds - Date.now()) / 86400000)) : null;
  // Aviso do teste grátis só na reta final (últimos 5 dias), não o mês inteiro
  const showTrialBanner = mostrarAvisoTrial(perfil, isExempt);
  const planExpiry       = perfil?.planExpiresAt?.toDate?.() || null;
  const diasAteVencer    = planExpiry ? Math.max(0, Math.ceil((planExpiry - Date.now()) / 86400000)) : null;
  const showExpiryBanner = !isExempt && PLANOS_PAGOS.includes(perfil?.plan) && diasAteVencer !== null && diasAteVencer <= 7;
  const GRACE_MS = 2 * 24 * 60 * 60 * 1000;
  const assinaturaExpirada = !isExempt && perfil && PLANOS_PAGOS.includes(perfil?.plan) && planExpiry && planExpiry < new Date();
  const cancelouEAindaNoPeríodo = perfil?.autoRenewCancelled && planExpiry && planExpiry >= new Date();
  const dentroDoGrace = assinaturaExpirada && !perfil?.autoRenewCancelled && (Date.now() - planExpiry.getTime()) <= GRACE_MS;
  const planExpirado = !isExempt && perfil && (
    perfil.plan === "expired" || perfil.plan === "deleted" ||
    (perfil.plan === "trial" && trialEnds && trialEnds < new Date()) ||
    (assinaturaExpirada && !dentroDoGrace && !cancelouEAindaNoPeríodo)
  );

  if (perfil?.plan === "banned") return (
    <div style={{ minHeight:"100dvh", background:"var(--pc-ui-060b18, #060b18)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <div style={{ textAlign:"center", padding:40, maxWidth:360 }}>
        <div style={{ fontSize:56, marginBottom:16 }}>🚫</div>
        <h2 style={{ color:"#fca5a5", fontWeight:900, fontSize:20, margin:"0 0 8px", letterSpacing:1 }}>Conta suspensa</h2>
        <p style={{ color:"#64748b", fontSize:13, margin:"0 0 8px", lineHeight:1.6 }}>Sua conta foi suspensa pelo administrador.</p>
        <p style={{ color:"#475569", fontSize:12, margin:"0 0 24px" }}>Entre em contato com o suporte:</p>
        <a href="mailto:printcontrol@gmail.com" style={{ display:"block", color:"var(--pc-ui-00d4aa, #00d4aa)", fontSize:12, marginBottom:24, textDecoration:"none" }}>printcontrol@gmail.com</a>
        <button onClick={logout} style={{ background:"none", border:"1px solid rgba(239,68,68,0.4)", color:"#ef4444", borderRadius:8, padding:"8px 20px", fontSize:12, fontWeight:700, cursor:"pointer" }}>✕ Sair da conta</button>
      </div>
    </div>
  );

  const precisaEscolherPlano = !isExempt && perfil && (!perfil.plan || perfil.plan === "pending");
  if (precisaEscolherPlano) return <EscolherPlano uid={userLogado.uid} perfil={perfil} onUpdate={setPerfil} onLogout={logout} />;
  if (planExpirado)         return <PlanoExpirado uid={userLogado.uid} perfil={perfil} onLogout={logout} onUpdate={setPerfil} />;

  if (isAdmin) return <AdminPanel notificacaoDestino={notificacaoDestino} onLogout={logout} />;

  /* ── Banners (compartilhado) ── */
  const banners = (
    <>
      {showTrialBanner && (
        <div style={{ background:diasTrial<=3?"rgba(255,71,87,0.12)":"rgba(255,165,2,0.1)", borderBottom:"1px solid "+(diasTrial<=3?C.danger:C.warning)+"44", padding:"7px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:12, color:diasTrial<=3?C.danger:C.warning, fontWeight:600 }}>
            {diasTrial>0 ? `⏳ Trial: ${diasTrial} dia${diasTrial!==1?"s":""} restante${diasTrial!==1?"s":""} — acesso completo, PDV e Loja inclusos` : "⚠️ Trial expirado — assine para continuar"}
          </span>
          <button onClick={() => abrirNotificacao({ tab: "config", secao: "conta", foco: "plano" })} style={{ background:"none", border:"1px solid "+(diasTrial<=3?C.danger:C.warning), color:diasTrial<=3?C.danger:C.warning, borderRadius:6, padding:"3px 12px", fontSize:11, fontWeight:700, cursor:"pointer" }}>Ver planos →</button>
        </div>
      )}
      {dentroDoGrace && (
        <div style={{ background:"rgba(255,71,87,0.12)", borderBottom:"1px solid rgba(255,71,87,0.35)", padding:"8px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:12, color:C.danger, fontWeight:600 }}>
            🔒 Assinatura expirou. Renove em até <strong>{Math.max(1, Math.ceil((GRACE_MS-(Date.now()-planExpiry.getTime()))/86400000))} dia(s)</strong>.
          </span>
          <button onClick={() => { setTab("config"); setShowMais(false); }} style={{ background:"none", border:"1px solid "+C.danger, color:C.danger, borderRadius:6, padding:"3px 12px", fontSize:11, fontWeight:700, cursor:"pointer" }}>Renovar →</button>
        </div>
      )}
      {showExpiryBanner && (
        <div style={{ background:"rgba(255,71,87,0.10)", borderBottom:"1px solid rgba(255,71,87,0.3)", padding:"7px 24px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:12, color:C.danger, fontWeight:600 }}>
            🔔 Assinatura expira em <strong>{diasAteVencer} dia{diasAteVencer!==1?"s":""}</strong>.
          </span>
          <button onClick={() => { setTab("config"); setShowMais(false); }} style={{ background:"none", border:"1px solid "+C.danger, color:C.danger, borderRadius:6, padding:"3px 12px", fontSize:11, fontWeight:700, cursor:"pointer" }}>Renovar →</button>
        </div>
      )}
    </>
  );

  const contentProps = { tab, coresCustom, marcasSalvas, addCor, addMarca, perfil, uid:userLogado.uid, onUpdate:setPerfil, onBadgeUpdate:setClientesBadge, onPersonalizar:() => { setTab("dashboard"); setEditandoAbas(true); }, isMobile, clientesGlobal, onGoTab:goTab, onAbrirBusca:abrirBusca, onIniciarTutorial:tour.start, tutorialCalculadora:tour.active && tour.step?.forceTab === "calc", notificacaoDestino, posicaoAbas:permitePosicaoAbas ? posicaoAbas : undefined, onPosicaoAbasChange:alterarPosicaoAbas, abasTravadas, onTravaAbasChange:alterarTravaAbas };

  const buscaModal = buscaAberta && (
    <>
      <div onClick={() => { setBuscaAberta(false); setBuscaQuery(""); }}
        style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:8000 }} />
      <div style={{ position:"fixed", top:64, left:"50%", transform:"translateX(-50%)", width:"min(600px,95vw)", zIndex:8001, display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", alignItems:"center", background:"var(--pc-ui-0a1628, #0a1628)", border:"1px solid rgba(0,212,170,0.35)", borderRadius:14, padding:"12px 16px", gap:10, boxShadow:"0 16px 48px rgba(0,0,0,0.7)" }}>
          <span style={{ fontSize:16 }}>🔍</span>
          <input autoFocus value={buscaQuery} onChange={e => setBuscaQuery(e.target.value)}
            placeholder="Buscar pedidos, clientes, produtos..."
            style={{ flex:1, background:"none", border:"none", outline:"none", color:"var(--pc-ui-f1f5f9, #f1f5f9)", fontSize:15 }} />
          {buscaQuery && <button onClick={() => setBuscaQuery("")} style={{ background:"none", border:"none", color:"#64748b", cursor:"pointer", fontSize:16, padding:0 }}>✕</button>}
          <kbd style={{ background:"var(--pc-ui-0d1f38, #0d1f38)", border:"1px solid var(--pc-white-10, rgba(255,255,255,0.1))", borderRadius:6, padding:"2px 8px", fontSize:11, color:"#64748b" }}>Esc</kbd>
        </div>
        {buscaQuery.trim() && (
          <div style={{ background:"var(--pc-ui-0a1628, #0a1628)", border:"1px solid var(--pc-white-8, rgba(255,255,255,0.08))", borderTop:"none", borderRadius:"0 0 14px 14px", overflow:"hidden", boxShadow:"0 16px 48px rgba(0,0,0,0.7)" }}>
            {buscaResultados.length === 0 ? (
              <div style={{ padding:"20px 20px", color:"#64748b", fontSize:13, textAlign:"center" }}>Nenhum resultado para "{buscaQuery}"</div>
            ) : buscaResultados.map((r, i) => (
              <div key={i} onClick={() => { setTab(r.tab); setShowMais(false); setBuscaAberta(false); setBuscaQuery(""); }}
                style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", cursor:"pointer", borderTop:"1px solid var(--pc-white-5, rgba(255,255,255,0.05))", transition:"background .15s" }}
                onMouseEnter={e => e.currentTarget.style.background="rgba(0,212,170,0.06)"}
                onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                <span style={{ fontSize:18, flexShrink:0 }}>{r.icon}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ color:"var(--pc-ui-f1f5f9, #f1f5f9)", fontSize:13, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.label}</div>
                  {r.sub && <div style={{ color:"#64748b", fontSize:11, marginTop:1 }}>{r.sub}</div>}
                </div>
                <span style={{ fontSize:10, color:"var(--pc-ui-334155, #334155)", background:"var(--pc-ui-0d1f38, #0d1f38)", borderRadius:6, padding:"2px 8px", flexShrink:0 }}>{r.tipo}</span>
              </div>
            ))}
          </div>
        )}
        {!buscaQuery.trim() && (
          <div style={{ background:"var(--pc-ui-0a1628, #0a1628)", border:"1px solid var(--pc-white-8, rgba(255,255,255,0.08))", borderTop:"none", borderRadius:"0 0 14px 14px", padding:"12px 16px", boxShadow:"0 16px 48px rgba(0,0,0,0.7)" }}>
            <div style={{ fontSize:11, color:"var(--pc-ui-334155, #334155)", fontWeight:600, letterSpacing:1, marginBottom:10 }}>ACESSO RÁPIDO</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {[{label:"Pedidos",id:"pedidos",icon:"📋"},{label:"Clientes",id:"clientes",icon:"👥"},{label:"PDV",id:"caixa",icon:"🛒"},{label:"Financeiro",id:"financeiro",icon:"💲"}].map(x => (
                <button key={x.id} onClick={() => { setTab(x.id); setShowMais(false); setBuscaAberta(false); }}
                  style={{ background:"var(--pc-ui-0d1f38, #0d1f38)", border:"1px solid var(--pc-white-8, rgba(255,255,255,0.08))", borderRadius:8, padding:"7px 14px", fontSize:12, color:"var(--pc-ui-94a3b8, #94a3b8)", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
                  {x.icon} {x.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );

  /* ══════════════════════════════════════════════════════════
     MOBILE / ANDROID LAYOUT
  ══════════════════════════════════════════════════════════ */
  const toolPopups = <>
    {modalClientes && <ToolDialog title="Clientes" subtitle="Contatos, relacionamento e novas oportunidades." icon="clientes" color="#72de99" onClose={() => setModalClientes(false)}><Clientes onBadgeUpdate={setClientesBadge} /></ToolDialog>}
    {modalAgenda && <ToolDialog title="Agenda" subtitle="Organize entregas e compromissos do seu estúdio." icon="agenda" color="#7ed7ff" onClose={() => setModalAgenda(false)}><Agenda /></ToolDialog>}
    {modalImpressoras && <ToolDialog title="Impressoras" subtitle="Sua produção começa com máquinas bem organizadas." icon="impressoras" color="#bf9bff" onClose={() => { setModalImpressoras(false); recarregarImpressoras(); }}><Impressoras /></ToolDialog>}
    {modalFilamentos && <ToolDialog title="Filamentos" subtitle="Cores, materiais e estoque prontos para a próxima ideia." icon="filamentos" color="#71e5ce" onClose={() => setModalFilamentos(false)}><Filamentos coresCustom={coresCustom} onAddCor={addCor} marcasSalvas={marcasSalvas} onAddMarca={addMarca} /></ToolDialog>}
    {modalMateriais && <ToolDialog title="Materiais" subtitle="Cuide dos insumos que completam seus produtos." icon="materiais" color="#edabf8" onClose={() => setModalMateriais(false)}><Materiais /></ToolDialog>}
    {modalNotas && <ToolDialog title="Anotações" subtitle="Ideias, listas e lembretes em um só lugar." icon="notas" color="#ffd17c" onClose={() => setModalNotas(false)}><NotasIA /></ToolDialog>}
  </>;

  if (isMobile) {
    const isMaisTab = MAIS_IDS.has(tab);
    return (
      <div className="app-mobile" style={{ height:"100dvh", display:"flex", flexDirection:"column", background:C.bg, color:C.text, fontFamily:"system-ui,-apple-system,sans-serif", overflow:"hidden" }}>
        <style>{`
          @keyframes spin    { to { transform: rotate(360deg) } }
          @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
          @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
          * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
          ::-webkit-scrollbar { width: 0; background: transparent; }
          .app-mobile button:focus-visible { outline:2px solid var(--pc-ui-00d4aa, #00d4aa); outline-offset:-3px; }
          .app-mobile-content { min-height:0; scroll-padding:20px 0 90px; }
          .app-mobile-header button { min-width:44px; min-height:44px; }
        `}</style>

        <ToastContainer />
        {buscaModal}
        {updateInfo && <UpdateModal updateInfo={updateInfo} downloadProgress={downloadProgress} />}
        {tour.active && (() => {
          const InteractiveComp = TOUR_INTERACTIVE_COMPONENTS[tour.step.kind];
          return InteractiveComp
            ? <InteractiveComp onDone={tour.next} />
            : <TourOverlay step={tour.step} index={tour.index} total={tour.total} onNext={tour.next} onBack={tour.index ? tour.back : undefined} onAction={() => { tour.skip(); abrirNotificacao({ tab:"config", secao:"suporte" }); }} onClose={tour.isAreaTour ? tour.skip : undefined} />;
        })()}
        {banners}

        {/* Top App Bar */}
        <header className="app-mobile-header pc-mobile-topbar">
          <CompanyBrand compact empresa={perfil?.empresa} logo={perfil?.logoUrl} subtitle={TAB_LABELS[tab] || "Print Control"} />
          <button type="button" className="mobile-shortcuts-trigger" aria-label="Atalhos" title="Atalhos" aria-expanded={showAtalhos} aria-haspopup="dialog" onClick={() => { setShowMais(false); setShowAtalhos(v=>!v); }}>
            <HeaderIcon name="grid" size={20} />
            {(clientesBadge > 0 || impressorasCount === 0) && <span className="mobile-shortcuts-trigger-alert" aria-label="Há novidades nos atalhos" />}
          </button>
        </header>

        {/* Content */}
        <main className="app-mobile-content" style={{ flex:1, overflowY:"auto", overflowX:"hidden", padding:`20px 12px ${tab !== "caixa" && tab !== "calc" && !isMaisTab ? 90 : 24}px`, WebkitOverflowScrolling:"touch" }}>
          <TabContent {...contentProps} />
        </main>

        {/* FAB — escondido no PDV (id "caixa") e em qualquer sub-página do
            hub "Mais" (Filamentos, Calculadora, Financeiro, etc.): nelas o
            usuário já está numa tela específica com suas próprias ações
            fixas no rodapé/canto, e o FAB (com atalhos redundantes como
            "Abrir PDV") ficava sobrepondo botões e o calendário de faturas. */}
        {tab !== "dashboard" && tab !== "caixa" && tab !== "calc" && !isMaisTab && (
          <>
            {fabOpen && (
              <div onClick={() => setFabOpen(false)}
                style={{ position:"fixed", inset:0, zIndex:150 }} />
            )}
            <div style={{ position:"fixed", bottom:"calc(70px + env(safe-area-inset-bottom,0px))", right:18, zIndex:151, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:10 }}>
              {fabOpen && [
                { label:"Novo Pedido",  icon:"📋", id:"pedidos"  },
                { label:"Abrir PDV",    icon:"🛒", id:"caixa"    },
                { label:"Novo Cliente", icon:"👥", id:"clientes" },
              ].map((a, i) => (
                <div key={a.id} onClick={() => { setTab(a.id); setShowMais(false); setFabOpen(false); }}
                  style={{ display:"flex", alignItems:"center", gap:10, background:C.card, border:"1px solid "+C.border, borderRadius:24, padding:"10px 18px 10px 14px", cursor:"pointer", boxShadow:"0 4px 20px rgba(0,0,0,0.5)", animation:`fabItemIn .2s ${i*0.05}s both cubic-bezier(.16,1,.3,1)` }}>
                  <span style={{ fontSize:18 }}>{a.icon}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:C.text, whiteSpace:"nowrap" }}>{a.label}</span>
                </div>
              ))}
              <button aria-label={fabOpen ? "Fechar ações rápidas" : "Abrir ações rápidas"} aria-expanded={fabOpen} onClick={() => { setFabOpen(v => !v); setShowMais(false); }}
                style={{ width:54, height:54, borderRadius:27, background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 6px 24px rgba(0,212,170,0.4)", fontSize:26, fontWeight:300, color:"var(--pc-ui-04111f, #04111f)", transition:"transform .2s", transform:fabOpen?"rotate(45deg)":"rotate(0deg)" }}>
                +
              </button>
            </div>
          </>
        )}
        <style>{`@keyframes fabItemIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}`}</style>

        {/* Bottom Navigation */}
        <nav aria-label="Navegação principal" style={{ background:C.panel, borderTop:"1px solid "+C.border, display:"flex", height:"calc(62px + env(safe-area-inset-bottom,0px))", flexShrink:0, paddingBottom:"env(safe-area-inset-bottom,0px)" }}>
          {BOTTOM_NAV.map(item => {
            const isActive = item.id === "mais" ? (showMais || isMaisTab) : tab === item.id;
            return (
              <button key={item.id} data-tour={`nav-${item.id}`} aria-current={isActive ? "page" : undefined} onClick={() => goTab(item.id)}
                style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3, background:"none", border:"none", cursor:"pointer", padding:"6px 2px", position:"relative", outline:"none" }}>
                {isActive && (
                  <div style={{ position:"absolute", top:0, width:36, height:3, borderRadius:"0 0 3px 3px", background:"var(--pc-ui-00d4aa, #00d4aa)" }} />
                )}
                <div style={{ width:44, height:28, display:"flex", alignItems:"center", justifyContent:"center", borderRadius:14, background:isActive?"rgba(0,212,170,0.12)":"transparent", transition:"background .2s" }}>
                  <I d={item.icon} s={20} c={isActive?"var(--pc-ui-00d4aa, #00d4aa)":C.muted} />
                </div>
                <span style={{ fontSize:10, color:isActive?"var(--pc-ui-00d4aa, #00d4aa)":C.muted, fontWeight:isActive?700:400, letterSpacing:0.2, lineHeight:1 }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* "Mais": hub de módulos agrupado por área, com rolagem vertical
            (mesmo padrão visual dos modais de Impressoras/Agenda/Clientes) */}
        {showMais && <MobileMore tab={tab} onGoTab={goTab} onClose={() => setShowMais(false)} onLogout={logout}
          isFavorito={isFavorito} toggleFavorito={toggleFavorito} clientesBadge={clientesBadge} consignacaoAtiva={consignacaoAtiva} />}

        {showAtalhos && <MobileHeaderShortcuts onClose={() => setShowAtalhos(false)} onAction={abrirAtalho} onLogout={logout} nome={userLogado.nome} avatar={userLogado.avatar} empresa={perfil?.empresa} clientesBadge={clientesBadge} impressorasCount={impressorasCount} />}
        {toolPopups}
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════
     DESKTOP LAYOUT (original)
  ══════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight:"100dvh", background:C.bg, color:C.text, fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      <ToastContainer />
      {buscaModal}
      {updateInfo && <UpdateModal updateInfo={updateInfo} downloadProgress={downloadProgress} />}
      {tour.active && (() => {
        const InteractiveComp = TOUR_INTERACTIVE_COMPONENTS[tour.step.kind];
        return InteractiveComp
          ? <InteractiveComp onDone={tour.next} />
          : <TourOverlay step={tour.step} index={tour.index} total={tour.total} onNext={tour.next} onBack={tour.index ? tour.back : undefined} onAction={() => { tour.skip(); abrirNotificacao({ tab:"config", secao:"suporte" }); }} onClose={tour.isAreaTour ? tour.skip : undefined} />;
      })()}
      {banners}

      <AppHeader empresa={perfil?.empresa} logo={perfil?.logoUrl} nome={userLogado.nome} avatar={userLogado.avatar}
        items={MOBILE_HEADER_SHORTCUTS} clientesBadge={clientesBadge} impressorasCount={impressorasCount}
        version={window.electronAPI ? APP_VERSION : undefined} onLogout={logout}
        active={{ clientes:modalClientes, agenda:modalAgenda, impressoras:modalImpressoras, filamentos:modalFilamentos, materiais:modalMateriais, notas:modalNotas, config:tab === "config" }}
        onAction={id => {
          const modais = { clientes:setModalClientes, agenda:setModalAgenda, impressoras:setModalImpressoras, filamentos:setModalFilamentos, materiais:setModalMateriais, notas:setModalNotas };
          if (modais[id]) modais[id](value => !value);
          else setTab(id);
        }}
        hint={showHint && <div className="pc-header-hint">
          <strong>Seu espaço, do seu jeito</strong>
          <p>{permitePosicaoAbas ? "Arraste as abas para mudar a ordem. Em Configurações → Aparência, escolha a posição do menu e trave o layout." : "Organize as abas em Configurações → Aparência → Reordenar Abas."}</p>
          <button type="button" onClick={() => { localStorage.setItem("3dp_hint_personalizar", "1"); setShowHint(false); }}>Entendi</button>
        </div>} />

      {/* Tabs */}
      <div style={{ display:"flex", flexDirection:abasLaterais?"row":"column", alignItems:"stretch" }}>
      {permitePosicaoAbas ? <WebNavigation tabs={tabsOrdem} activeTab={tab} lateral={abasLaterais} locked={abasTravadas} onSelect={setTab} onReorder={(index, destination) => {
        if (abasTravadas || index < 0 || destination < 0 || index >= tabsOrdem.length || destination >= tabsOrdem.length) return;
        const next = [...tabsOrdem];
        next.splice(destination, 0, next.splice(index, 1)[0]);
        setTabsOrdem(next);
        try { localStorage.setItem("3dp_tabs_ordem", JSON.stringify(next.map(item => item.id))); } catch {}
      }} /> : (
      <nav aria-label="Abas principais" style={{ background:C.panel, borderBottom:abasLaterais?"none":"1px solid "+C.border, borderRight:abasLaterais?"1px solid "+C.border:"none", padding:abasLaterais?"16px 10px":"0 8px 0 24px", display:"flex", flexDirection:abasLaterais?"column":"row", gap:abasLaterais?6:0, overflowX:"auto", alignItems:"stretch", flexShrink:0, width:abasLaterais?190:undefined, position:abasLaterais?"sticky":undefined, top:abasLaterais?0:undefined, alignSelf:abasLaterais?"flex-start":undefined, maxHeight:abasLaterais?"100dvh":undefined, overflowY:abasLaterais?"auto":undefined }}>
        {tabsOrdem.map((t, idx) => {
          const mover = (dir) => {
            const next = [...tabsOrdem];
            next.splice(idx+dir, 0, next.splice(idx, 1)[0]);
            setTabsOrdem(next);
            localStorage.setItem("3dp_tabs_ordem", JSON.stringify(next.map(x => x.id)));
          };
          return (
            <div key={t.id} style={{ position:"relative", display:"inline-flex", flexShrink:0 }}>
              {editandoAbas && idx > 0 && (
                <button aria-label={`Mover ${t.label} para antes`} onClick={e => { e.stopPropagation(); mover(-1); }}
                  style={{ position:"absolute", top:3, left:2, background:"rgba(0,212,170,0.12)", border:"1px solid rgba(0,212,170,0.3)", borderRadius:4, color:C.accent2, fontSize:9, lineHeight:1, padding:"2px 4px", cursor:"pointer", zIndex:1 }}>{abasLaterais ? "▲" : "◀"}</button>
              )}
              <button data-tour={`nav-${t.id}`} aria-current={tab===t.id?"page":undefined} onClick={() => setTab(t.id)}
                style={{ width:abasLaterais?"100%":undefined, textAlign:abasLaterais?"left":undefined, background:abasLaterais && tab===t.id?"rgba(0,212,170,0.08)":"none", border:"none", borderRadius:abasLaterais?8:0, borderLeft:abasLaterais?(tab===t.id?"2px solid "+TAB_COLOR[t.id]:"2px solid transparent"):undefined, borderBottom:abasLaterais?"none":tab===t.id?"2px solid "+TAB_COLOR[t.id]:"2px solid transparent", color:tab===t.id?TAB_COLOR[t.id]:C.muted, padding:editandoAbas?"22px 18px 13px":"13px 14px", fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", transition:"padding .2s" }}>
                {t.icon} {t.label}
                {t.id === "clientes" && clientesBadge > 0 && (
                  <span style={{ marginLeft:5, background:"#ef4444", color:"#fff", borderRadius:10, fontSize:9, fontWeight:900, padding:"1px 5px", verticalAlign:"middle" }}>
                    {clientesBadge}
                  </span>
                )}
              </button>
              {editandoAbas && idx < tabsOrdem.length-1 && (
                <button aria-label={`Mover ${t.label} para depois`} onClick={e => { e.stopPropagation(); mover(1); }}
                  style={{ position:"absolute", top:3, right:2, background:"rgba(0,212,170,0.12)", border:"1px solid rgba(0,212,170,0.3)", borderRadius:4, color:C.accent2, fontSize:9, lineHeight:1, padding:"2px 4px", cursor:"pointer", zIndex:1 }}>{abasLaterais ? "▼" : "▶"}</button>
              )}
            </div>
          );
        })}
        {editandoAbas && (
          <button onClick={() => setEditandoAbas(false)}
            style={{ marginLeft:"auto", alignSelf:"center", flexShrink:0, background:"linear-gradient(135deg,var(--pc-ui-00d4aa, #00d4aa),var(--pc-ui-0ea5e9, #0ea5e9))", border:"none", borderRadius:8, color:"var(--pc-ui-04111f, #04111f)", fontSize:12, fontWeight:800, padding:"7px 18px", cursor:"pointer", letterSpacing:1, whiteSpace:"nowrap" }}>
            ✓ OK
          </button>
        )}
      </nav>
      )}

      {/* Content */}
      <div style={{ flex:1, minWidth:0 }}>
      <div data-content style={{ maxWidth:tab === "calc" ? 1200 : 980, margin:"0 auto", padding:"28px 20px" }}>
        <TabContent {...contentProps} />
      </div>
      </div>
      </div>

      {toolPopups}
    </div>
  );
}
